// Per-epoch line/text content shared by the hover tooltip, the pinned card,
// and the plain-text copy payload, so the three never diverge.
//
// Every value renders through the null-honest formatters (null -> em dash);
// a gated cluster statistic never renders as a dash with a separate note —
// the verbatim written reason IS the value, in place of the withheld
// figure, everywhere: tooltip, pin card, copy payload.
//
// Rows are grouped by basis (BASE / NET INCL MEV) rather than repeating the
// basis in every label: the group header carries that context once. In
// single-basis (net-only, pre-0.7.0) mode there is one ungrouped block.

import { formatCount, formatPct, formatUtcMonthDay, formatUtcMonthDayTime } from "./format";
import {
  distributionCoverage,
  distributionCoverageInfl,
  type DistributionCoverage,
  type EpochJoined,
} from "./series";
import type { ThemeTokens } from "./theme";

/** Which delegator-APY series a tooltip/pin/copy render reads: net (incl. MEV, default) or base (inflation-only). */
export type TooltipBasis = "net" | "base";

export interface TooltipLine {
  label: string;
  value: string;
  /** Legend swatch color; undefined for provenance/position lines. */
  color?: string;
}

/**
 * One basis's row group. `label`/`marker` are null for the single-group
 * (net-only, pre-0.7.0) case, where there is nothing to separate; both are
 * set ("BASE"/"base", "NET INCL MEV"/"net") in the dual base+net view.
 */
export interface TooltipRowGroup {
  label: string | null;
  marker: string | null;
  rows: TooltipLine[];
}

export interface EpochTooltipView {
  groups: TooltipRowGroup[];
  /** Basis-independent trailing lines (e.g. the operator-APR gap note), rendered after every group. */
  trailing: TooltipLine[];
}

const COVERAGE_THRESHOLD_PCT = 50;

/**
 * Written reason for an epoch whose cluster distribution is not computed yet.
 *
 * The daemon measures an epoch's distribution during the FOLLOWING epoch, so
 * the newest epoch normally carries our own yield with no cluster context —
 * permanently, not transiently. Terse because it stands in for every withheld
 * slot in the group, the same way the gate's reason does.
 */
export const PENDING_REASON = "not yet measured (lands next epoch)";

/**
 * Verbatim written reason for a coverage-gated epoch; used inline wherever
 * the gated figure would render. `verb` distinguishes the tooltip/pin/copy
 * grammar ("withheld") from the position strip's caption grammar ("gated"),
 * per the spec's literal quoted strings for each surface.
 */
export function coverageGateReason(
  measured: number,
  total: number | null,
  verb: "withheld" | "gated" = "withheld",
): string {
  if (total === null || total <= 0) {
    return `cluster statistics ${verb}: coverage unverifiable (validator total unknown)`;
  }
  // Zero measured is absence, not a thin sample: "withheld: 0/797 (0.0%),
  // threshold 50%" reads as a malfunction where "not measured" is the fact.
  if (measured === 0) {
    return `cluster statistics not measured for this epoch (0 of ${formatCount(total)} validators)`;
  }
  const pct = (Math.floor((measured / total) * 1000) / 10).toFixed(1);
  return (
    `cluster statistics ${verb}: only ${formatCount(measured)} of ${formatCount(total)} measured ` +
    `(${pct}%), below the ${COVERAGE_THRESHOLD_PCT}% coverage floor`
  );
}

/**
 * "Jul 8 05:12 to Jul 10 09:44 UTC", "in progress" when the epoch has no
 * end_time yet, or "dates not recorded" when there is no income row at
 * all — a genuine in-progress epoch and a data gap are not the same claim.
 * "not recorded" states the fact (our crawler holds no record for this
 * epoch) without the alarm of "unavailable" in title position.
 */
export function epochDateRangeText(joined: EpochJoined | undefined): string {
  const income = joined?.income;
  if (income === undefined) {
    return "dates not recorded";
  }
  if (income.end_time === null) {
    return "in progress";
  }
  return `${formatUtcMonthDayTime(income.start_time)} to ${formatUtcMonthDayTime(income.end_time)} UTC`;
}

/** "Epoch 998 · Jul 8 05:12 to Jul 10 09:44 UTC" — the tooltip/pin card header. */
export function epochHeader(epoch: number, joined: EpochJoined | undefined): string {
  return `Epoch ${epoch} · ${epochDateRangeText(joined)}`;
}

/** "Epoch 998 · Jul 8 to Jul 10" — the compact pin-card header form (date only, no time). */
export function epochHeaderShort(epoch: number, joined: EpochJoined | undefined): string {
  const income = joined?.income;
  if (income === undefined) {
    return `Epoch ${epoch} · dates not recorded`;
  }
  if (income.end_time === null) {
    return `Epoch ${epoch} · in progress`;
  }
  return `Epoch ${epoch} · ${formatUtcMonthDay(income.start_time)} to ${formatUtcMonthDay(income.end_time)}`;
}

/**
 * Position as a ratio to a cluster reference: how much more (or less) yield
 * than the reference validator, in percent.
 *
 * Replaces a four-bucket word ("at or above p90") that pinned a consistent
 * top-decile validator to one bucket forever — coarse, and it discarded the
 * precise standing the widget already serves. The ratio is a documented
 * derivation of two served values (ours / reference - 1), reads intuitively,
 * and moves every epoch.
 *
 * The caller prefers the cluster AVERAGE (`delegator_apy_infl_avg_pct`, schema
 * v36) and falls back to the median: the mean sits below the median (a lower
 * tail of delinquent validators pulls it down), so "% above the average" is
 * the larger, more intuitive figure, and `refLabel` names whichever reference
 * was available so the number can never be read against the wrong baseline.
 */
export function ratioVsCluster(ours: number, reference: number, refLabel: string): string {
  if (reference <= 0) {
    return "—";
  }
  return formatClusterRatioPct((ours / reference - 1) * 100, refLabel);
}

/**
 * Format an already-computed relative delta (percent) against a cluster
 * reference: "+14.6% above <label>", "3.1% below <label>", or "at the <label>"
 * within a hair of parity. Split from {@link ratioVsCluster} so a caller that
 * already holds an aggregate (e.g. a windowed median of per-epoch ratios) can
 * render the same grammar without a second ours/reference division.
 */
export function formatClusterRatioPct(pct: number, refLabel: string): string {
  if (Math.abs(pct) < 0.05) {
    return `at the ${refLabel}`;
  }
  return pct > 0
    ? `+${pct.toFixed(1)}% above ${refLabel}`
    : `${Math.abs(pct).toFixed(1)}% below ${refLabel}`;
}

/**
 * Band range text with degeneracy-adaptive precision: "6.05 - 6.05 %" (a
 * tight top decile collapsing at 2 dp) is a riddle, so escalate to 3 dp
 * when the rendered endpoints tie; if they tie at 3 dp as well the region
 * is effectively a point — state that instead of a zero-width range.
 */
export function bandRangeText(p90: number, max: number): string {
  if (p90.toFixed(2) !== max.toFixed(2)) {
    return `${p90.toFixed(2)} - ${max.toFixed(2)} %`;
  }
  if (p90.toFixed(3) !== max.toFixed(3)) {
    return `${p90.toFixed(3)} - ${max.toFixed(3)} %`;
  }
  return `${max.toFixed(2)} % (p90 = max)`;
}

/** One basis's ours/median/band/position/coverage row set. */
interface GroupFields {
  ourColor: string;
  ours: number | null;
  p50Raw: number | null;
  p90Raw: number | null;
  maxRaw: number | null;
  /** Cluster mean base APY (base basis only; null for net — no net mean served). */
  avgRaw: number | null;
  cov: DistributionCoverage | null;
  /** Row exists, distribution not computed yet (lands during the next epoch). */
  pending: boolean;
}

/**
 * Builds one basis's row group content. `style: "primary"` (the BASE group,
 * or the single group in net-only mode) always renders median/band/position/
 * coverage, with the gate's reason text standing in for all four when
 * withheld — same "reason IS the value" rule as before, now consolidated.
 * `style: "secondary"` (the NET INCL MEV group) is terser: no position row,
 * and the band row is omitted (not reason-valued) when not shown, so a
 * gated net side does not triple the same reason text.
 */
function basisGroupRows(f: GroupFields, t: ThemeTokens, style: "primary" | "secondary"): TooltipLine[] {
  const rows: TooltipLine[] = [{ label: "ours", value: formatPct(f.ours), color: f.ourColor }];

  // Pending epoch: median, band, position and coverage all wait on the SAME
  // input — the cluster distribution, computed during the following epoch —
  // so they resolve together. Collapse them into one line instead of
  // repeating the reason four times. Four "not yet measured" lines read as a
  // total void on an epoch whose own figure ("ours", above) IS measured and
  // whose vote-credit standing is measured too (trailing, in buildEpochView).
  // Guard is `cov === null && pending`, not `cov === null`: a missing cluster
  // row entirely (true absence) still falls through to the em-dash form.
  if (f.cov === null && f.pending) {
    rows.push({ label: "cluster comparison", value: PENDING_REASON });
    return rows;
  }

  const gateOpen = f.cov !== null && f.cov.sufficient;
  // A withheld measurement (thin coverage) is not an unmade one; the pending
  // case is handled by the collapse above, so only the gate reason remains.
  const reason = f.cov !== null && !f.cov.sufficient ? coverageGateReason(f.cov.measured, f.cov.total) : null;
  const p50 = gateOpen ? f.p50Raw : null;
  const p90 = gateOpen ? f.p90Raw : null;
  const max = gateOpen ? f.maxRaw : null;
  const bandShown = p90 !== null && max !== null;

  if (style === "primary") {
    rows.push({ label: "median", value: gateOpen ? formatPct(p50) : (reason ?? "—"), color: t.medianLine });
    rows.push({
      label: "band p90-max",
      value: bandShown ? bandRangeText(p90 as number, max as number) : (reason ?? "—"),
      color: t.band,
    });
    // Prefer the cluster AVERAGE (larger, more intuitive figure); fall back to
    // the median when the mean is not served (older API/epoch). Both gate with
    // the distribution, so an insufficient-coverage epoch shows the reason.
    const avg = gateOpen ? f.avgRaw : null;
    const posRef =
      avg !== null
        ? { value: avg, label: "cluster average" }
        : p50 !== null
          ? { value: p50, label: "cluster median" }
          : null;
    rows.push({
      label: "position",
      value:
        f.ours !== null && posRef !== null
          ? ratioVsCluster(f.ours, posRef.value, posRef.label)
          : (reason ?? "—"),
    });
  } else if (bandShown) {
    rows.push({
      label: "band p90-max",
      value: bandRangeText(p90 as number, max as number),
      color: t.band,
    });
  }

  // Pending is handled by the collapse above, so coverage here is either a
  // real n/N (covered), the gate reason (gated), or an em dash (no cluster row).
  let coverage = "—";
  if (f.cov !== null) {
    const total = f.cov.total === null ? "?" : formatCount(f.cov.total);
    coverage = f.cov.sufficient ? `n=${formatCount(f.cov.measured)}/${total}` : (reason ?? "—");
  }
  rows.push({ label: "coverage", value: coverage });

  return rows;
}

/**
 * Measured vote-credit (participation) standing for the epoch, or null.
 *
 * Distinct from the yield percentile (base percentile / our_base_rank): this
 * is computed from CREDITS, known at epoch close, so it is available on the
 * pending epoch when the yield comparison is not. Surfacing it there keeps a
 * just-closed epoch from reading as a void and reconciles the widget with the
 * operator's own digest, which leads with this rank. Labelled "vote-credit"
 * and "(measured)" so it is never mistaken for the pending yield rank.
 */
function voteCreditRankLine(joined: EpochJoined | undefined): TooltipLine | null {
  const r = joined?.rank;
  if (r === undefined || r.rank === null || r.validators === null || r.validators <= 0) {
    return null;
  }
  return { label: "vote-credit rank", value: `${formatCount(r.rank)} of ${formatCount(r.validators)} (measured)` };
}

/**
 * Vote credits earned this epoch and the gap to the top validator — the exact,
 * on-chain-checkable standing fact ("N credits below the #1 validator"). A pro
 * reads it; the operator/engineers can reconcile it against getVoteAccounts.
 */
function creditsGapLine(joined: EpochJoined | undefined): TooltipLine | null {
  const r = joined?.rank;
  if (r === undefined || r.credits_earned === null || r.gap_to_top === null) {
    return null;
  }
  const gap = r.gap_to_top === 0 ? "cluster top" : `${formatCount(r.gap_to_top)} below top`;
  return { label: "vote credits", value: `${formatCount(r.credits_earned)} · ${gap}` };
}

/**
 * `basis` defaults to "net" (single ungrouped block, pre-0.7.0 behavior).
 * `basis: "base"` — once the API serves the inflation-only columns —
 * renders two groups: BASE (primary, charcoal ours) then NET INCL MEV
 * (secondary, violet ours), separated by a hairline in the rendered forms
 * below. Both groups independently coverage-gate (item 3 of the base/net
 * rewire: "still coverage-gated with their own disclosure").
 */
/**
 * `netRevealed` gates the secondary NET INCL MEV group (base basis only). The
 * net line is hidden on the chart by default (opt-in via the legend / N key),
 * so on the default view there is no net line riding the base band — and the
 * group's comparability caveat ("cluster band is base-only; this net figure
 * is not directly comparable") would be warning about a comparison that is
 * not on screen, on every hover and even on pending epochs where no band is
 * drawn at all. The group and its caveat now appear only when the net line
 * is actually revealed: the tooltip reflects what is plotted, and the caveat
 * sits beside the line it is about. No effect on the net-only (pre-base) mode.
 */
export function buildEpochView(
  joined: EpochJoined | undefined,
  t: ThemeTokens,
  basis: TooltipBasis = "net",
  netRevealed = true,
): EpochTooltipView {
  const apy = joined?.apy;
  const cluster = joined?.cluster;

  // No trailing operator-APR disclosure: this widget renders no operator-APR
  // figure, so disclosing that figure's absence is a non sequitur here — a
  // disclosure belongs on the surface that serves the value. (Backfilled
  // epochs have no boundary stake capture, so the old note fired on every
  // pre-tracking epoch, a year of noise.)
  const trailing: TooltipLine[] = [];

  // On a pending epoch the cluster comparison collapses to one line; surface
  // the measured vote-credit standing there so the epoch is not all absence
  // and the widget agrees with the digest's leading rank. Only when pending:
  // on a resolved epoch the yield percentile is the headline cluster fact.
  const netPending = cluster !== undefined && distributionCoverage(cluster) === null;
  const basePending = cluster !== undefined && distributionCoverageInfl(cluster) === null;

  if (basis !== "base") {
    const rows = basisGroupRows(
      {
        ourColor: t.ourLine,
        ours: apy?.delegator_apy_pct ?? null,
        p50Raw: cluster?.delegator_apy_p50_pct ?? null,
        p90Raw: cluster?.delegator_apy_p90_pct ?? null,
        maxRaw: cluster?.delegator_apy_max_pct ?? null,
        avgRaw: null,
        cov: distributionCoverage(cluster),
        pending: netPending,
      },
      t,
      "primary",
    );
    const voteRank = netPending ? voteCreditRankLine(joined) : null;
    if (voteRank !== null) {
      trailing.push(voteRank);
    }
    const credits = creditsGapLine(joined);
    if (credits !== null) {
      trailing.push(credits);
    }
    return { groups: [{ label: null, marker: null, rows }], trailing };
  }

  const baseRows = basisGroupRows(
    {
      ourColor: t.ourLine,
      ours: apy?.delegator_apy_inflation_pct ?? null,
      p50Raw: cluster?.delegator_apy_infl_p50_pct ?? null,
      p90Raw: cluster?.delegator_apy_infl_p90_pct ?? null,
      maxRaw: cluster?.delegator_apy_infl_max_pct ?? null,
      avgRaw: cluster?.delegator_apy_infl_avg_pct ?? null,
      cov: distributionCoverageInfl(cluster),
      pending: cluster !== undefined && distributionCoverageInfl(cluster) === null,
    },
    t,
    "primary",
  );

  // Percentile row: appended only when the API serves all three of its own
  // computed figures for this epoch (our_base_percentile_pct, our_base_rank,
  // distribution_infl_validators as the rank's denominator) — absent
  // (never a dash) on API versions/epochs that do not carry the column.
  const pct = cluster?.our_base_percentile_pct ?? null;
  const rank = cluster?.our_base_rank ?? null;
  const totalM = cluster?.distribution_infl_validators ?? null;
  if (pct !== null && rank !== null && totalM !== null) {
    baseRows.push({
      label: "base percentile",
      value: `${pct.toFixed(1)} (rank ${formatCount(rank)} of ${formatCount(totalM)})`,
    });
  }
  const groups: TooltipRowGroup[] = [{ label: "BASE", marker: "base", rows: baseRows }];

  // NET INCL MEV group only when the net line is revealed on the chart (see
  // the netRevealed doc above): otherwise the group's comparability caveat
  // warns about a net-vs-base-band comparison the reader cannot make.
  if (netRevealed) {
    const netRows = basisGroupRows(
      {
        ourColor: t.netLine,
        ours: apy?.delegator_apy_pct ?? null,
        p50Raw: cluster?.delegator_apy_p50_pct ?? null,
        p90Raw: cluster?.delegator_apy_p90_pct ?? null,
        maxRaw: cluster?.delegator_apy_max_pct ?? null,
        avgRaw: null,
        cov: distributionCoverage(cluster),
        pending: cluster !== undefined && distributionCoverage(cluster) === null,
      },
      t,
      "secondary",
    );
    // The chart's cluster band is BASE-only, so this net figure riding above
    // it is not a like-for-like reference. Stated once, beside the net line
    // it is about, only while that line is on the chart.
    netRows.push({
      label: "comparability",
      value: "cluster band is base-only; this net (incl MEV) figure is not directly comparable",
    });
    groups.push({ label: "NET INCL MEV", marker: "net", rows: netRows });
  }

  // Base is the primary basis here, so its pending state governs whether the
  // measured vote-credit standing fills the collapsed comparison.
  const voteRank = basePending ? voteCreditRankLine(joined) : null;
  if (voteRank !== null) {
    trailing.push(voteRank);
  }
  const credits = creditsGapLine(joined);
  if (credits !== null) {
    trailing.push(credits);
  }

  return { groups, trailing };
}

export function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function rowHtml(l: TooltipLine): string {
  const swatch =
    l.color !== undefined
      ? `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;` +
        `margin-right:6px;background:${l.color};"></span>`
      : `<span style="display:inline-block;width:14px;"></span>`;
  return (
    `<div style="display:flex;justify-content:space-between;gap:16px;">` +
    `<span>${swatch}${escapeHtml(l.label)}</span>` +
    `<span style="font-variant-numeric:tabular-nums;">${escapeHtml(l.value)}</span></div>`
  );
}

/** HTML for the ECharts hover tooltip formatter: header, then each group (uppercase muted label, hairline separator between groups), then trailing lines. */
export function viewToHtml(title: string, view: EpochTooltipView, t: ThemeTokens): string {
  const groupHtml = (g: TooltipRowGroup): string => {
    const header =
      g.label !== null
        ? `<div style="font-size:10px;font-weight:600;color:${t.textMuted};` +
          `text-transform:uppercase;letter-spacing:0.05em;margin:8px 0 2px;">${escapeHtml(g.label)}</div>`
        : "";
    return header + g.rows.map(rowHtml).join("");
  };
  const separator = `<div style="border-top:1px solid ${t.frameBorder};margin:8px 0;"></div>`;
  const groupsHtml = view.groups.map(groupHtml).join(separator);
  const trailingHtml = view.trailing.map(rowHtml).join("");
  return (
    `<div style="font-size:12px;line-height:1.5;color:${t.text};">` +
    `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(title)}</div>${groupsHtml}${trailingHtml}</div>`
  );
}

/**
 * Plain-text pin-card copy payload: label-tab-value lines of the rendered
 * real values plus epoch and date range, grouped with `[base]`/`[net]`
 * section markers mirroring the tooltip/pin grouping. Withheld statistics
 * already serialize as "withheld: <reason>" inline (basisGroupRows); never
 * a blank cell or a zero.
 */
export function buildEpochCopyPayload(epoch: number, joined: EpochJoined | undefined, view: EpochTooltipView): string {
  const rows = [`Epoch\t${epoch}`, `Date range\t${epochDateRangeText(joined)}`];
  for (const g of view.groups) {
    if (g.marker !== null) {
      rows.push(`[${g.marker}]`);
    }
    for (const l of g.rows) {
      rows.push(`${l.label}\t${l.value}`);
    }
  }
  for (const l of view.trailing) {
    rows.push(`${l.label}\t${l.value}`);
  }
  return rows.join("\n");
}
